import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { useProject } from "../../contexts/ProjectContext";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import * as XLSX from "xlsx";
import { Button } from "../../components/ui/button";
import {
  Package,
  Activity,
  CheckCircle2,
  ClipboardList,
  TrendingUp,
  Download,
  FileSpreadsheet,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  RadialBarChart,
  RadialBar,
} from "recharts";
import StoreSelfieCollages from "../../components/StoreSelfieCollages";import PieChartCard from "../../components/charts/PieChartCard";
import BarChartCard from "../../components/charts/BarChartCard";
import LineChartCard from "../../components/charts/LineChartCard";
import ComposedChartCard from "../../components/charts/ComposedChartCard";
import { PALETTE, tooltipStyle } from "../../components/charts/palette";

function Stat({ label, value, icon: Icon, testid }) {
  return (
    <div className="glass-panel p-4 sm:p-6 min-w-0" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 truncate">
          {label}
        </div>
        <Icon className="h-4 w-4 text-neutral-400 shrink-0" />
      </div>
      <div className="font-display font-bold text-2xl sm:text-4xl mt-2 sm:mt-4 tracking-tight">{value}</div>
    </div>
  );
}

function Panel({ title, subtitle, children, testid }) {
  return (
    <div className="glass-panel p-4 sm:p-5" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="font-display font-bold text-lg tracking-tight">{title}</div>
          {subtitle && (
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-0.5">
              {subtitle}
            </div>
          )}
        </div>
      </div>
      <div>{children}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { projectId } = useProject();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recent, setRecent] = useState([]);
  const stockTableRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const downloadStockTable = async () => {
    if (!stockTableRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(stockTableRef.current, {
        backgroundColor: "#ffffff",
        cacheBust: true,
        pixelRatio: 2,
      });
      const link = document.createElement("a");
      const date = analytics?.today_stock_grid?.date || new Date().toISOString().slice(0, 10);
      link.download = `today-stock-${date}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Stock table downloaded");
    } catch (e) {
      toast.error("Failed to download image");
    } finally {
      setDownloading(false);
    }
  };

  const downloadStockExcel = () => {
    const grid = analytics?.today_stock_grid;
    if (!grid || !grid.rows || grid.rows.length === 0) {
      toast.error("No stock data to export");
      return;
    }
    const stores = grid.stores || [];
    const aoa = [];
    aoa.push(["SKU", ...stores, "Total"]);
    grid.rows.forEach((r) => {
      aoa.push([r.sku, ...stores.map((st) => r[st] ?? 0), r.total ?? 0]);
    });
    const totals = grid.totals || {};
    aoa.push(["TOTAL", ...stores.map((st) => totals[st] ?? 0), totals.__grand__ ?? 0]);
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    // Column widths
    ws["!cols"] = [{ wch: 28 }, ...stores.map(() => ({ wch: 14 })), { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stock");
    const date = grid.date || new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `today-stock-${date}.xlsx`);
    toast.success("Excel downloaded");
  };

  useEffect(() => {
    const load = async () => {
      try {
        const pq = projectId && projectId !== "__all" ? `&project_id=${projectId}` : "";
        const shiftQ = projectId && projectId !== "__all" ? `?project_id=${projectId}` : "";
        const [s, a, sh] = await Promise.all([
          api.get(`/admin/stats${shiftQ}`),
          // Use a very wide range to cover "all days"
          api.get(`/admin/analytics?days=365${pq}`),
          api.get(`/admin/shifts${shiftQ ? `${shiftQ}&` : "?"}limit=8`),
        ]);
        setStats(s.data);
        setAnalytics(a.data);
        setRecent(sh.data.slice(0, 8));
      } catch (e) {
        toast.error(formatApiError(e));
      }
    };
    load();
    // Live refresh every 60s; skip when the tab is hidden.
    const t = setInterval(() => {
      if (!document.hidden) load();
    }, 60000);
    return () => clearInterval(t);
  }, [projectId]);

  const dailyData = useMemo(
    () =>
      (analytics?.daily || []).slice(-30).map((d) => ({
        ...d,
        label: d.date.slice(5),
      })),
    [analytics],
  );

  const conv = analytics?.conversion || { surveys_total: 0, surveys_with_purchase: 0, rate_percent: 0 };
  const radialData = [
    {
      name: "Conversion",
      value: conv.rate_percent,
      fill: PALETTE[2],
    },
  ];

  return (
    <div className="space-y-5 sm:space-y-8">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <Stat label="Conversion rate" value={`${conv.rate_percent ?? 0}%`} icon={TrendingUp} testid="stat-conversion-rate" />
        <Stat label="UBBS units sold" value={analytics?.totals?.units_sold_ubbs ?? "—"} icon={Package} testid="stat-units-sold-ubbs" />
        <Stat label="Customers surveyed" value={analytics?.totals?.surveys ?? "—"} icon={ClipboardList} testid="stat-customers-surveyed" />
        <Stat label="Active shifts" value={stats?.active_shifts ?? "—"} icon={Activity} testid="stat-active-shifts" />
        <Stat label="Today check-ins" value={stats?.today_checkins ?? "—"} icon={CheckCircle2} testid="stat-today-checkins" />
      </div>

      <StoreSelfieCollages />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Nationality */}
        <PieChartCard
          title="Nationality mix"
          subtitle="All stores · all days"
          testid="chart-nationality"
          data={analytics?.by_nationality || []}
          inner={50}
          outer={100}
        />

        {/* Conversion radial */}
        <Panel
          title="Conversion rate"
          subtitle={`${conv.surveys_with_purchase} of ${conv.surveys_total} surveys converted`}
          testid="chart-conversion"
        >
          <div className="h-72 flex items-center justify-center relative">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart
                cx="50%"
                cy="50%"
                innerRadius="62%"
                outerRadius="100%"
                barSize={28}
                data={radialData}
                startAngle={90}
                endAngle={-270}
              >
                <RadialBar background dataKey="value" cornerRadius={4} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <TrendingUp className="h-5 w-5 text-emerald-600 mb-1" />
              <div className="font-display font-bold text-5xl tracking-tight">
                {conv.rate_percent}%
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-1">
                Converted
              </div>
            </div>
          </div>
        </Panel>
      </div>

      {/* Survey distribution row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <PieChartCard
          title="Heard about UBBS"
          subtitle="How customers found out"
          testid="chart-heard"
          data={analytics?.by_heard || []}
          inner={55}
          outer={100}
          emptyText="No survey data yet"
        />

        <PieChartCard
          title="Nicotine level"
          subtitle="Preferred strength"
          testid="chart-nicotine"
          data={analytics?.by_nicotine_level || []}
          inner={0}
          outer={100}
          emptyText="No survey data yet"
        />
      </div>

      {/* Today live · per active outlet */}
      <ComposedChartCard
        title="Today's sales — by outlet (live)"
        subtitle="Auto-refresh every 30s · bar = units sold today · line = surveys captured today"
        testid="chart-today-sales-by-store"
        data={analytics?.today_sales_by_store || []}
        barKey="units"
        lineKey="surveys"
        barLabel="Units sold"
        lineLabel="Surveys"
      />

      {/* Sales by outlet */}
      <LineChartCard
        title="Sales by outlet · total units"
        subtitle="All SKUs · units sold (from surveys)"
        testid="chart-sales-by-store-total"
        data={analytics?.sales_by_store || []}
        height="h-60 sm:h-72"
      />

      <BarChartCard
        title="Sales by SKU · total units"
        subtitle="Units sold per SKU (from surveys, all dates)"
        testid="chart-sales-by-sku"
        data={analytics?.sales_by_sku || []}
        height="h-72 sm:h-96"
        xAxisAngle={-30}
      />

      {/* Current product mix — pie with side legend */}
      <PieChartCard
        title="Current product mix"
        subtitle="What customers currently use (all surveys)"
        testid="chart-current-product"
        data={analytics?.by_current_product || []}
        height="h-72 sm:h-96"
        inner={0}
        outer={140}
      />

    </div>
  );
}
